import { Box, Card, Grid, Skeleton, Stack } from "@mui/material";
import FlexBox from "../../components/layouts/Flexbox";
import { createDummyArray } from "../../services/utils";

type ProjectDetailsSkeletonProps = {
  drawerLayout?: boolean;
};

export default function ProjectDetailsSkeleton({
  drawerLayout = true,
}: ProjectDetailsSkeletonProps) {
  const content = (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        <FlexBox justifyContent="space-between" alignItems="center">
          <Skeleton variant="text" width="40%" height={36} />
          <Skeleton
            variant="rounded"
            width={100}
            height={28}
            sx={{ borderRadius: 2 }}
          />
        </FlexBox>
      </Grid>

      <Grid item xs={12}>
        <Stack gap={0.5}>
          <Skeleton variant="text" width={80} height={20} />
          <Skeleton variant="text" width="60%" height={28} />
          <Skeleton variant="text" width="85%" height={22} />
        </Stack>
      </Grid>

      <Grid item xs={12}>
        <Card variant="outlined" sx={{ p: 2 }}>
          <Stack gap={2}>
            <Stack direction="row" alignItems="center" gap={1}>
              <Skeleton variant="circular" width={20} height={20} />
              <Skeleton variant="text" width={60} height={18} />
              <Skeleton variant="text" width="70%" height={22} />
            </Stack>
            <FlexBox alignItems="center" gap={5}>
              <Stack direction="row" alignItems="center" gap={1}>
                <Skeleton variant="circular" width={20} height={20} />
                <Skeleton variant="text" width={40} height={18} />
                <Skeleton variant="text" width={80} height={22} />
              </Stack>
              <Stack direction="row" alignItems="center" gap={1}>
                <Skeleton variant="circular" width={20} height={20} />
                <Skeleton variant="text" width={70} height={18} />
                <Skeleton variant="text" width={50} height={22} />
              </Stack>
            </FlexBox>
          </Stack>
        </Card>
      </Grid>

      <Grid item xs={12}>
        <Skeleton
          variant="rounded"
          width="30%"
          height={24}
          sx={{ borderRadius: 1, mb: 1.5 }}
        />
        <Skeleton variant="text" width="55%" height={24} sx={{ mb: 1 }} />
        <Skeleton
          variant="rounded"
          width={100}
          height={28}
          sx={{ borderRadius: 14, mb: 2 }}
        />
        <Skeleton
          variant="rounded"
          width="100%"
          height={12}
          sx={{ borderRadius: 1, mb: 1.5 }}
        />
        <FlexBox gap={2} flexWrap="wrap">
          {createDummyArray(4).map((_, i) => (
            <Stack key={i} direction="row" alignItems="center" gap={0.5}>
              <Skeleton variant="rounded" width={12} height={12} />
              <Skeleton variant="text" width={60} height={20} />
            </Stack>
          ))}
        </FlexBox>
      </Grid>

      <Grid item xs={12}>
        <Skeleton
          variant="rounded"
          width="55%"
          height={24}
          sx={{ borderRadius: 1, mb: 1.5 }}
        />
        <Grid container spacing={2}>
          {createDummyArray(2).map((_, i) => (
            <Grid item xs={12} sm={6} key={i}>
              <Card variant="outlined" sx={{ p: 2 }}>
                <Stack gap={1}>
                  <Skeleton variant="text" width="80%" height={22} />
                  <Skeleton
                    variant="rounded"
                    width={70}
                    height={26}
                    sx={{ borderRadius: 14 }}
                  />
                </Stack>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Grid>

      <Grid item xs={12}>
        <Skeleton variant="text" width="60%" height={24} sx={{ mb: 1 }} />
        <Skeleton
          variant="rounded"
          width="25%"
          height={40}
          sx={{ borderRadius: 1, mb: 2 }}
        />
        <Grid container spacing={2}>
          {createDummyArray(2).map((_, i) => (
            <Grid item xs={12} sm={6} key={i}>
              <Card variant="outlined" sx={{ p: 2 }}>
                <Stack gap={1}>
                  <Skeleton variant="text" width="75%" height={22} />
                  <Stack direction="row" alignItems="center" gap={0.5}>
                    <Skeleton
                      variant="rounded"
                      width={80}
                      height={26}
                      sx={{ borderRadius: 14 }}
                    />
                    <Skeleton variant="circular" width={20} height={20} />
                  </Stack>
                </Stack>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Grid>
    </Grid>
  );

  if (!drawerLayout) {
    return <Box pb={2}>{content}</Box>;
  }

  return (
    <Stack height="100%" minHeight={0}>
      <Box
        sx={{
          overflowY: "auto",
          overflowX: "hidden",
        }}
        flex={1}
        pb={2}
      >
        {content}
      </Box>

      <Box
        position="sticky"
        bottom={0}
        zIndex={10}
        bgcolor="background.paper"
        borderTop={1}
        borderColor="divider"
        pt={2}
        mx={-2}
        px={2}
      >
        <FlexBox gap={2} justifyContent="flex-end">
          <Skeleton variant="rounded" width={100} height={40} sx={{ borderRadius: 1 }} />
          <Skeleton variant="rounded" width={120} height={40} sx={{ borderRadius: 1 }} />
        </FlexBox>
      </Box>
    </Stack>
  );
}
